// Builds the analytics Excel export (three tabs mirroring the product's
// spreadsheet model): Per Learner, Anonymous Survey, Overall Stats.
//
// The row builders are pure so tests can assert on cell values without
// touching the browser download path; `downloadWorkbook` is the only
// side-effectful entry point (dynamic import keeps the xlsx writer out of
// the main bundle).
import type {
  AudienceOverallStats,
  CourseLearnerMetrics,
  SurveyAnswer,
} from '../../types/analytics';

/** write-excel-file cell in row-array mode. */
export interface WorkbookCell {
  value: string | number;
  fontWeight?: 'bold';
  wrap?: boolean;
}

export type WorkbookRow = WorkbookCell[];

const NA = 'N/A';

const header = (value: string): WorkbookCell => ({ value, fontWeight: 'bold' });

const cell = (value: string | number | null | undefined): WorkbookCell => ({
  value: value === null || value === undefined || value === '' ? NA : value,
});

const yesNo = (value: boolean): WorkbookCell => ({ value: value ? 'Yes' : 'No' });

const minutes = (seconds: number | null | undefined): number | null =>
  seconds === null || seconds === undefined || seconds === 0
    ? null
    : Math.round((seconds / 60) * 10) / 10;

/** "2026-06-05T10:00:00" → "2026-06-05 10:00" (dates pass through). */
const dateTime = (iso: string | null): string | null => {
  if (!iso) return null;
  const [date, time] = iso.split('T');
  return time ? `${date} ${time.slice(0, 5)}` : date;
};

const surveyValue = (answer: SurveyAnswer | undefined): WorkbookCell =>
  cell(answer?.value ?? null);

/** Disambiguate repeated quiz titles ("Quiz", "Quiz" → "Quiz (1)", "Quiz (2)"). */
const quizDisplayTitles = (data: CourseLearnerMetrics): Map<string, string> => {
  const counts = new Map<string, number>();
  data.quizzes.forEach((quiz) => {
    counts.set(quiz.title, (counts.get(quiz.title) ?? 0) + 1);
  });
  const seen = new Map<string, number>();
  const titles = new Map<string, string>();
  data.quizzes.forEach((quiz) => {
    if ((counts.get(quiz.title) ?? 0) > 1) {
      const n = (seen.get(quiz.title) ?? 0) + 1;
      seen.set(quiz.title, n);
      titles.set(quiz.module_id, `${quiz.title} (${n})`);
    } else {
      titles.set(quiz.module_id, quiz.title);
    }
  });
  return titles;
};

export const buildPerLearnerSheet = (data: CourseLearnerMetrics): WorkbookRow[] => {
  const displayTitle = quizDisplayTitles(data);
  const head: WorkbookRow = [
    header('Name'),
    header('Email'),
    header('Department'),
    header('Role'),
    header('Enrollment date'),
    header('End date'),
    header('Course completion (Yes/No)'),
    header('Course progress (%)'),
    header('First activity date'),
    header('Last activity date'),
    header('Time spent (min)'),
    header('Start to finish (min)'),
    header('Completion efficiency (%)'),
  ];
  data.quizzes.forEach((quiz) => {
    const title = displayTitle.get(quiz.module_id) ?? quiz.title;
    head.push(
      header(`Test score — ${title} (%)`),
      header(`Attempts — ${title}`),
      header(`Avg. time per attempt — ${title} (min)`),
      header(`Avg. time per question — ${title} (min)`),
      header(`Learning efficiency — ${title} (%/min)`),
      header(`Attempt improvement — ${title} (pts)`)
    );
  });
  data.survey.questions.forEach((question) => {
    head.push(header(`Survey — ${question.label}`));
  });

  const rows = data.learners.map((learner) => {
    // A re-assigned learner keeps one row per enrollment; suffix the name so
    // repeated rows are self-explanatory ("Ada Lovelace (enrollment 2 of 2)").
    const displayName =
      learner.enrollment_count > 1
        ? `${learner.name ?? ''} (enrollment ${learner.enrollment_index} of ${learner.enrollment_count})`
        : learner.name;
    const row: WorkbookRow = [
      cell(displayName),
      cell(learner.email),
      cell(learner.department),
      cell(learner.role),
      cell(learner.enrollment_date),
      cell(learner.deadline),
      yesNo(learner.completed),
      cell(learner.progress_percent),
      cell(dateTime(learner.started_at)),
      cell(dateTime(learner.last_activity_at)),
      cell(minutes(learner.time_spent.active_seconds)),
      cell(minutes(learner.time_spent.wallclock_seconds)),
      cell(learner.completion_efficiency),
    ];
    const byQuiz = new Map(learner.quizzes.map((quiz) => [quiz.module_id, quiz]));
    data.quizzes.forEach((quiz) => {
      const result = byQuiz.get(quiz.module_id);
      row.push(
        cell(result?.score ?? null),
        cell(result && result.attempts > 0 ? result.attempts : null),
        cell(minutes(result?.avg_time_per_attempt_seconds)),
        cell(minutes(result?.avg_time_per_question_seconds)),
        cell(result?.learning_efficiency ?? null),
        cell(result?.attempt_improvement ?? null)
      );
    });
    const byItem = new Map(learner.survey_answers.map((answer) => [answer.item_id, answer]));
    data.survey.questions.forEach((question) => {
      row.push(surveyValue(byItem.get(question.item_id)));
    });
    return row;
  });

  return [head, ...rows];
};

export const buildAnonymousSurveySheet = (data: CourseLearnerMetrics): WorkbookRow[] => {
  const { questions, responses } = data.anonymous_survey;
  if (questions.length === 0) {
    return [[cell('No anonymous survey in this course.')]];
  }
  const head: WorkbookRow = questions.map((question) => header(question.label));
  const rows = responses.map((response) => {
    const byItem = new Map(response.map((answer) => [answer.item_id, answer]));
    return questions.map((question) => surveyValue(byItem.get(question.item_id)));
  });
  return [head, ...rows];
};

// Metric guide (first tab) — mirrors the product's spreadsheet model:
// [metric, what it tells you, how it is measured]. Keep wording concise.
const GUIDE_ROWS: Array<[string, string, string]> = [
  [
    'Repeat enrollments',
    'A learner assigned the same course more than once keeps one row per enrollment.',
    'Rows are marked "(enrollment 1 of 2)", "(enrollment 2 of 2)", … oldest first. The Overall Stats tab and the analytics dashboard count each learner once, using their most recent enrollment. Survey answers are stored per learner, so they repeat on each of that learner’s rows.',
  ],
  [
    'Course completion (Yes/No)',
    'Whether the learner has finished every module of the course.',
    'Yes once all modules are completed. While it reads No, metrics the learner has not reached yet show N/A.',
  ],
  [
    'Course progress (%)',
    'How far the learner is right now.',
    'Completed modules ÷ total modules × 100 — the same figure as the learner’s progress bar, captured at export time.',
  ],
  [
    'First activity date',
    'When the learner first worked on the course.',
    'Earliest recorded module activity.',
  ],
  [
    'Last activity date',
    'When the learner last worked on the course.',
    'Most recent recorded module activity.',
  ],
  [
    'Time spent (min)',
    'Total time actively spent on the course.',
    'Sum of active time across modules; pauses and time away are not counted.',
  ],
  [
    'Start to finish (min)',
    'Elapsed time from opening the first module to completing the last.',
    'Includes pauses and days away, so it can far exceed Time spent. Only filled once the course is completed.',
  ],
  [
    'Completion efficiency (%)',
    'The trainer’s planned duration compared with the time the learner actually needed.',
    'Planned duration ÷ actual active time × 100, shown once the course is completed. Above 100 means faster than planned.',
  ],
  [
    'Test score (%)',
    'The learner’s result on each quiz in the course.',
    'Final score of the latest graded attempt, as recorded by the platform.',
  ],
  [
    'Attempts',
    'How many attempts the learner used on the quiz.',
    'Count of attempts taken.',
  ],
  [
    'Avg. time per attempt (min)',
    'How long one attempt took on average.',
    'Total quiz time ÷ number of attempts.',
  ],
  [
    'Avg. time per question (min)',
    'How long the learner spent per question on average.',
    'Total quiz time ÷ (attempts × number of questions).',
  ],
  [
    'Learning efficiency (%/min)',
    'Score earned per minute spent on the quiz; higher means more efficient.',
    'Final score ÷ quiz time in minutes. E.g. 80% in 20 minutes = 4%/min.',
  ],
  [
    'Attempt improvement (pts)',
    'How many percentage points the score changed between the first and last attempt.',
    'Last attempt score − first attempt score. E.g. 40% then 80% = +40 pts; a drop shows negative. Needs at least two attempts; attempts made before tracking began show N/A.',
  ],
  [
    'Survey result',
    'The learner’s survey answers, one column per question.',
    'Star ratings appear as numbers, open questions as text. Anonymous surveys appear on the Anonymous Survey tab — shuffled and never linked to a learner.',
  ],
  [
    'Note — N/A',
    'A metric that does not apply or has no data yet.',
    'E.g. the course has no test, the quiz has not been taken, or no end date was set.',
  ],
  [
    'Note — Overall Stats',
    'One row per assigned group, plus one per individually enrolled learner.',
    'Averages include only learners who have the metric; the three completion percentages cover the whole audience and add up to 100.',
  ],
];

export const buildGuideSheet = (): WorkbookRow[] => [
  [header('Metric'), header('Description'), header('How it is measured')],
  ...GUIDE_ROWS.map(([metric, description, measured]) => [
    cell(metric),
    { ...cell(description), wrap: true },
    { ...cell(measured), wrap: true },
  ]),
];

const failedQuestionSummary = (audience: AudienceOverallStats): string | null => {
  if (audience.most_failed_questions.length === 0) return null;
  return audience.most_failed_questions
    .map((question) => `${question.label} (${question.fail_count}×)`)
    .join('; ');
};

export const buildOverallStatsSheet = (data: CourseLearnerMetrics): WorkbookRow[] => {
  // Content edits rewrite what percentages MEAN — the stamp lets a trainer
  // interpret a sudden dip ("the course changed on Jul 3").
  const modified = data.course.content_last_modified_at;
  const contextRows: WorkbookRow[] = modified
    ? [
        [
          header('Course content last modified'),
          cell(new Date(modified).toLocaleDateString()),
        ],
        [],
      ]
    : [];
  const head: WorkbookRow = [
    header('Audience'),
    header('Learners'),
    header('Most failed questions'),
    header('Avg. time spent (min)'),
    header('Completed by end date (%)'),
    header('Completed after end date (%)'),
    header('Still not completed (%)'),
    header('Avg. final score (%)'),
    header('Best test score (%)'),
    header('Worst test score (%)'),
    header('Learning efficiency average (%/min)'),
    header('Attempt improvement average (pts)'),
    header('Avg. satisfaction (/5)'),
  ];
  const rows = data.overall.audiences.map((audience) => [
    cell(audience.name),
    cell(audience.learner_count),
    cell(failedQuestionSummary(audience)),
    cell(minutes(audience.avg_time_spent_seconds)),
    cell(audience.completion_in_deadline_percent),
    cell(audience.completion_out_of_deadline_percent),
    cell(audience.not_completed_percent),
    cell(audience.avg_final_score),
    cell(audience.best_final_score),
    cell(audience.worst_final_score),
    cell(audience.avg_learning_efficiency),
    cell(audience.avg_attempt_improvement),
    cell(audience.avg_satisfaction),
  ]);
  return [...contextRows, head, ...rows];
};

export const SHEET_NAMES = ['Guide', 'Per Learner', 'Anonymous Survey', 'Overall Stats'];

export const buildWorkbook = (data: CourseLearnerMetrics): WorkbookRow[][] => [
  buildGuideSheet(),
  buildPerLearnerSheet(data),
  buildAnonymousSurveySheet(data),
  buildOverallStatsSheet(data),
];

/** Generate and download the .xlsx in the browser. */
export const downloadWorkbook = async (data: CourseLearnerMetrics): Promise<void> => {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const safeTitle = data.course.title.replace(/[^a-z0-9]+/gi, '-');
  const workbook = buildWorkbook(data);
  await writeXlsxFile(
    SHEET_NAMES.map((name, index) => ({ name, data: workbook[index] })),
  ).toFile(`${safeTitle}-analytics.xlsx`);
};
